import { useSuspenseQuery } from '@tanstack/react-query';
import { useGetFulfillmentSummaryById, useGetDirectFulfillmentSummaryById } from './order';
import { FulfillmentProduct, useFulfillmentContext } from '@/modules/fulfillment/context/FulfillmentContext';
import { useDeferredValue, useMemo } from 'react';
import { isEqual, pick } from 'lodash-es';
import { useDecorateOrganizationByOrganizationIds } from '@/clients/organization/Organization.ts';

const toSummaryPayload = (products: FulfillmentProduct[]) => ({
    products: products.flatMap(({ id, quote, category }) => {
        if (!quote) return [];
        return {
            productId: id,
            purchasePrice: quote.purchasePrice.toNumber(),
            salesPrice: quote.salesPrice.toNumber(),
            quantity: quote.amount,
            productName: quote.name,
            unit: quote.unit,
            orderingOrganizationId: quote.orderingOrganizationId,
            currencyCode: quote.currencyCode,
            name: quote.name,
            partnerOrganizationId: quote.partnerOrganizationId,
            positionProductId: id,
            productCategory: category,
            sflxPlatformId: quote.platformOrganizationId,
        };
    }),
});

const allowedPropsForFulfillmentSummary = [
    'productId',
    'quantity',
    'unit',
    'purchasePrice',
    'salesPrice',
    'productName',
    'orderingOrganizationId',
] as const;

export const useFulfillmentSummary = () => {
    const { getFulfillmentSummaryById } = useGetFulfillmentSummaryById();
    const { getDirectFulfillmentSummary } = useGetDirectFulfillmentSummaryById();
    const { decorateOrganizationByOrganizationIds } = useDecorateOrganizationByOrganizationIds();

    const { contextSource, order, orderFulfillmentProducts, products } = useFulfillmentContext();

    const relevantProductsData = useMemo(
        () => (contextSource === 'order' ? toSummaryPayload(orderFulfillmentProducts) : toSummaryPayload(products)),
        [contextSource, orderFulfillmentProducts, products]
    );

    const deferredRelevantProductsData = useDeferredValue(relevantProductsData);

    const isUpdating = useMemo(
        () => !isEqual(relevantProductsData, deferredRelevantProductsData),
        [deferredRelevantProductsData, relevantProductsData]
    );

    return Object.assign(
        useSuspenseQuery({
            queryKey: ['fulfillment-summary', order.id, deferredRelevantProductsData, contextSource],
            queryFn: async () => {
                const summaryData =
                    contextSource === 'order'
                        ? await getFulfillmentSummaryById(order.id, {
                              products: deferredRelevantProductsData.products.map(p =>
                                  pick(p, allowedPropsForFulfillmentSummary)
                              ),
                          })
                        : await getDirectFulfillmentSummary(order.id, deferredRelevantProductsData);
                return await decorateOrganizationByOrganizationIds(summaryData.organizations);
            },
            refetchOnWindowFocus: false,
        }),
        { isUpdating }
    );
};
